import { useState } from 'react'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import {
  bandejaDeCierre,
  type CandidatoDeCierre,
} from '@/features/rh/api/bandejaDeCierre'
import {
  ACCIONES,
  mensajeDeFallo,
  opcionesDeMotivo,
  precargaDeDescarte,
  type AccionDeCierre,
} from '@/features/rh/lib/bandejaDeCierre'
import { MOTIVOS_DESCARTE } from '@/features/rh/lib/reclutamientoTablero'
import { esAvisoDeSobrecupo } from '@/features/rh/lib/coberturaDeVacante'

export type CandidatoParaCierre = Pick<CandidatoDeCierre, 'candidateId' | 'fullName'> & {
  directionDecision?: CandidatoDeCierre['directionDecision']
}

interface ModalDeAccionDeCierreProps {
  accion: AccionDeCierre
  candidato: CandidatoParaCierre
  onCerrar: () => void
  /** El servidor aceptó la acción. */
  onHecho: () => void
  /** Falló por otra razón que el aviso de plazas cubiertas. */
  onFallo: (mensaje: string, esConflicto: boolean) => void
}

/**
 * La confirmación de una decisión de cierre de RH: seleccionar, mandar a
 * Dirección o descartar lo que Dirección rechazó.
 *
 * Es la misma para la pantalla de Cierre y para las tarjetas del tablero, así
 * que las dos piden lo mismo (motivo al descartar, aviso de plazas cubiertas) y
 * llaman al mismo servicio. Se monta solo mientras hay algo que confirmar: el
 * motivo precargado de Dirección se toma al abrirse.
 */
export const ModalDeAccionDeCierre = ({
  accion,
  candidato,
  onCerrar,
  onHecho,
  onFallo,
}: ModalDeAccionDeCierreProps) => {
  // Al descartar lo que Dirección rechazó se precargan su motivo y su
  // comentario (SRH-311): RH confirma lo que Dirección dijo y no lo escribe de nuevo.
  const precarga =
    accion === 'descartar'
      ? precargaDeDescarte(candidato.directionDecision)
      : { motivo: '', comentario: '' }
  const [motivo, setMotivo] = useState(precarga.motivo)
  const [comentario, setComentario] = useState(precarga.comentario)
  const [trabajando, setTrabajando] = useState(false)
  // El aviso del servidor cuando la vacante ya tiene sus plazas cubiertas
  // (SRH-313). Mientras tenga texto el modal pide confirmar «de todos modos».
  const [sobrecupo, setSobrecupo] = useState<string | null>(null)

  const definicion = ACCIONES.find((a) => a.id === accion)
  const faltaMotivo = accion === 'descartar' && !motivo
  if (!definicion) return null

  const ejecutar = async () => {
    const id = candidato.candidateId
    setTrabajando(true)
    try {
      if (accion === 'seleccionar') {
        await bandejaDeCierre.seleccionar(id, sobrecupo !== null)
      }
      if (accion === 'enviar_direccion') await bandejaDeCierre.enviarADireccion(id)
      if (accion === 'descartar') {
        await bandejaDeCierre.descartar(id, {
          reason: motivo,
          comment: comentario.trim() || undefined,
        })
      }
      onHecho()
    } catch (error: unknown) {
      // Plazas ya cubiertas: se advierte y se deja decidir. El modal sigue abierto.
      if (accion === 'seleccionar' && esAvisoDeSobrecupo(error)) {
        setSobrecupo(
          (error as { message?: string }).message ??
            'La vacante ya tiene las plazas cubiertas.',
        )
        setTrabajando(false)
        return
      }
      const e = error as { statusCode?: number; status?: number }
      onFallo(mensajeDeFallo(error), (e?.statusCode ?? e?.status) === 409)
    } finally {
      setTrabajando(false)
    }
  }

  return (
    <CapaDeModal onClickFondo={trabajando ? undefined : onCerrar}>
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <h2 className="text-base font-semibold text-[#1E1E1F]">{definicion.etiqueta}</h2>
        <p className="text-sm text-gray-500 mt-1">{definicion.confirmacion}</p>
        <p className="text-sm text-gray-700 mt-3">
          Candidato: <span className="font-medium">{candidato.fullName}</span>
        </p>

        {sobrecupo && (
          <p
            role="alert"
            className="mt-4 rounded-lg bg-[#FEF6E2] px-3 py-2.5 text-sm text-[#795E1B]"
          >
            {sobrecupo}
          </p>
        )}

        {accion === 'descartar' && (
          <>
            <div className="mt-4">
              <label
                htmlFor="motivo-descarte"
                className="block text-xs font-medium text-[#777777] mb-1.5"
              >
                Motivo <span className="text-red-500">*</span>
              </label>
              <SelectBuscable
                variante="formulario"
                id="motivo-descarte"
                value={motivo}
                onChange={setMotivo}
                placeholder="Selecciona el motivo"
                placeholderDeBusqueda="Buscar motivo..."
                opciones={opcionesDeMotivo(MOTIVOS_DESCARTE, precarga.motivo).map((m) => ({
                  value: m,
                  label: m,
                }))}
              />
            </div>

            <div className="mt-4">
              <label
                htmlFor="comentario-cierre"
                className="block text-xs font-medium text-[#777777] mb-1.5"
              >
                Comentario <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <textarea
                id="comentario-cierre"
                rows={3}
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-[#477FFA]"
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onCerrar}
            disabled={trabajando}
            className={estiloDeBoton('secundario')}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void ejecutar()}
            disabled={trabajando || faltaMotivo}
            className={estiloDeBoton(accion === 'descartar' ? 'peligro' : 'primario')}
          >
            {trabajando
              ? 'Guardando…'
              : sobrecupo
                ? 'Seleccionar de todos modos'
                : 'Confirmar'}
          </button>
        </div>
      </div>
    </CapaDeModal>
  )
}
